import { Link } from "react-router-dom";
import { FLAVORS, FLAVOR_NOTE, FLAVOR_HOW_TO, FLAVOR_SIDE_CARDS } from "@/data/flavors";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag, ElementBadge } from "@/components/common";
import { Eye, GitCommit, HeartHandshake, ShieldAlert, Edit3 } from "lucide-react";

const HOW_TO_ICONS = [Eye, GitCommit, HeartHandshake, ShieldAlert, Edit3];

export default function Flavors() {
  return (
    <div className="space-y-10">
      <PageHeader
        eyebrow="Guia dos Sabores na MTC"
        title="Sabores, elementos e equilíbrio"
        subtitle="Na Medicina Tradicional Chinesa, os sabores são observados como qualidades energéticas dos alimentos. Eles ajudam a entender relações, mas não substituem orientação profissional ou nutricional."
      >
        <div className="mt-3">
          <SourceTag source={{ pdfPage: 39, printedPage: "37" }} />
        </div>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Lista de Sabores */}
        <div className="lg:col-span-8 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            {FLAVORS.map((f) => {
              const c = elementColor(f.elemento);
              return (
                <div key={f.id} data-testid={`flavor-card-${f.id}`} className="overflow-hidden rounded-3xl border border-border bg-card">
                  <div className="flex items-center justify-between p-6" style={{ backgroundColor: c.soft }}>
                    <div>
                      <h3 className="font-serif text-2xl font-semibold" style={{ color: c.text }}>{f.sabor}</h3>
                      <p className="text-sm" style={{ color: c.primary }}>{f.orgaos}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <ElementBadge elementKey={f.elemento} />
                      <SourceTag source={f.source} />
                    </div>
                  </div>
                  <div className="grid gap-4 p-6 sm:grid-cols-2">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Quando usado com equilíbrio</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground">{f.equilibrio}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Excesso pode pesar</p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.excesso}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cartões Laterais do PDF: Use com consciência & Não é prescrição */}
        <div className="lg:col-span-4 space-y-5">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h4 className="font-serif text-lg font-semibold text-foreground">{FLAVOR_SIDE_CARDS.consciencia.titulo}</h4>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {FLAVOR_SIDE_CARDS.consciencia.texto}
            </p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            <h4 className="font-serif text-lg font-semibold text-foreground">{FLAVOR_SIDE_CARDS.naoPrescricao.titulo}</h4>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {FLAVOR_SIDE_CARDS.naoPrescricao.texto}
            </p>
          </div>
        </div>
      </div>

      {/* Como usar esta página */}
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <h4 className="font-serif text-xl font-medium text-foreground text-center sm:text-left">Como usar esta página</h4>
        <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {FLAVOR_HOW_TO.map((passo, i) => {
            const Icon = HOW_TO_ICONS[i] || Eye;
            return (
              <div key={i} className="flex flex-col items-center sm:items-start text-center sm:text-left rounded-2xl bg-secondary/40 p-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold mb-2">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-xs text-muted-foreground">{passo}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-secondary/50 p-6 text-center">
        <p className="text-sm italic text-muted-foreground leading-relaxed">
          {FLAVOR_SIDE_CARDS.frase} Veja também os{" "}
          <Link to="/alimentos" className="text-gold font-medium hover:underline">alimentos por elemento</Link>.
        </p>
      </div>

      <SafetyNote>{FLAVOR_NOTE}</SafetyNote>
    </div>
  );
}
